import { useMemo, useState } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';
import type { Event, PlanningItem, EntityAttachment } from '@peles/shared';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';
import { useEntityAttachmentsForEntities } from '../../hooks/useEntityAttachments';

export interface ItemizedPrintFilter {
  label: string;
  values: string[];
}

/** Selectable report columns. `photo` embeds each row's image attachments. */
type ColumnKey =
  | 'photo'
  | 'category'
  | 'item'
  | 'room'
  | 'quantity'
  | 'unitPrice'
  | 'total'
  | 'expense'
  | 'vendor';

const NON_PHOTO_COLUMNS: ColumnKey[] = [
  'category',
  'item',
  'room',
  'quantity',
  'unitPrice',
  'total',
  'expense',
  'vendor',
];

/** Canonical left-to-right column order (Photo leads when shown). */
const COLUMN_ORDER: ColumnKey[] = ['photo', ...NON_PHOTO_COLUMNS];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  photo: 'Photo',
  category: 'Category',
  item: 'Item',
  room: 'Room',
  quantity: 'Qty',
  unitPrice: 'Unit Price',
  total: 'Total',
  expense: 'Expense',
  vendor: 'Vendor',
};

const RIGHT_ALIGNED = new Set<ColumnKey>(['quantity', 'unitPrice', 'total']);
const MUTED_COLUMNS = new Set<ColumnKey>(['category', 'room', 'expense', 'vendor']);
/** The item name is the backbone of the report and can't be removed. */
const REQUIRED_COLUMN: ColumnKey = 'item';

interface Props {
  event: Event & {
    eventTypeName?: string;
    venueName?: string;
    logoUrl?: string | null;
  };
  /** Rows to print — already filtered by the caller. */
  items: PlanningItem[];
  /** Active filters shown in the report header (only non-empty ones). */
  activeFilters: ItemizedPrintFilter[];
  /**
   * Polymorphic entity type the items' attachments live under (e.g.
   * `'PLANNING_ITEM'`). When provided, a "Photo" column becomes selectable
   * and embeds each row's image attachments into the printed report.
   */
  attachmentEntityType?: string;
  onClose: () => void;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(d: Date | string | null | undefined) {
  if (!d) return '—';
  const dt = new Date(d);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString(
    'en-US',
    { month: 'long', day: 'numeric', year: 'numeric' },
  );
}

/**
 * Escape a value for interpolation into the print HTML. Quotes are escaped
 * too so values used inside double-quoted attributes (filenames, URLs) can't
 * break out and inject markup into the same-origin print window.
 */
function esc(s: string | null | undefined) {
  if (!s) return '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function itemLabel(item: PlanningItem): string {
  return item.itemName ?? item.catalogItemName ?? '—';
}

/** True for attachments we can embed as an `<img>` in the report (S3 images). */
function isImageAttachment(att: EntityAttachment): boolean {
  return !!att.downloadUrl && !!att.mimeType?.startsWith('image/');
}

/** Plain-text value for a (non-photo) column. */
function columnText(col: ColumnKey, item: PlanningItem): string {
  switch (col) {
    case 'category':
      return item.vendorCategoryName ?? '—';
    case 'item':
      return itemLabel(item);
    case 'room':
      return item.roomName ?? '—';
    case 'quantity':
      return String(item.quantity);
    case 'unitPrice':
      return fmt(item.unitPrice);
    case 'total':
      return fmt(item.total);
    case 'expense':
      return item.expenseItemName ?? '—';
    case 'vendor':
      return item.expenseVendorName ?? '—';
    default:
      return '';
  }
}

/**
 * Open the print window once every embedded image has settled (loaded or
 * errored), so photo cells are never blank in the generated PDF. A safety
 * timeout prevents a stuck image from blocking printing forever.
 */
function printWhenReady(win: Window) {
  const images: HTMLImageElement[] =
    win.document && win.document.images ? Array.from(win.document.images) : [];
  const pending = images.filter((img) => !img.complete);

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    win.focus();
    win.print();
  };

  if (pending.length === 0) {
    finish();
    return;
  }

  let remaining = pending.length;
  const settle = () => {
    remaining -= 1;
    if (remaining <= 0) finish();
  };
  pending.forEach((img) => {
    img.addEventListener('load', settle);
    img.addEventListener('error', settle);
  });
  // Fallback: never let a slow/stuck image hold the print dialog hostage.
  setTimeout(finish, 8000);
}

export function ItemizedPrintModal({
  event,
  items,
  activeFilters,
  attachmentEntityType,
  onClose,
}: Props) {
  const { data: appLogoUrl } = useAppSettingValue('app_logo_s3_key');
  // Every column except Photo is on by default; Photo is opt-in.
  const [selected, setSelected] = useState<Set<ColumnKey>>(() => new Set(NON_PHOTO_COLUMNS));

  const photoSelected = selected.has('photo') && !!attachmentEntityType;

  const itemIds = useMemo(() => items.map((i) => i.id), [items]);
  const { data: attachments = [], isLoading: photosLoading } = useEntityAttachmentsForEntities(
    attachmentEntityType ?? '',
    itemIds,
    photoSelected,
  );

  // Group image attachments by the planning item they belong to.
  const photosByItem = useMemo(() => {
    const map = new Map<string, EntityAttachment[]>();
    for (const att of attachments) {
      if (!isImageAttachment(att)) continue;
      const list = map.get(att.entityId) ?? [];
      list.push(att);
      map.set(att.entityId, list);
    }
    return map;
  }, [attachments]);

  // Photo column only renders once its fetch resolves (avoids an empty column).
  const showPhotoColumn = photoSelected && !photosLoading;

  const availableColumns = attachmentEntityType ? COLUMN_ORDER : NON_PHOTO_COLUMNS;
  const visibleColumns = COLUMN_ORDER.filter((c) =>
    c === 'photo' ? showPhotoColumn : selected.has(c),
  );
  const showTotalRow = selected.has('total');

  const toggleColumn = (key: ColumnKey) => {
    if (key === REQUIRED_COLUMN) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const logoSrc =
    event.logoUrl ?? (typeof appLogoUrl === 'string' ? appLogoUrl : null) ?? '/logo.png';

  const filters = activeFilters.filter((f) => f.values.length > 0);
  const grandTotal = items.reduce((sum, i) => sum + i.total, 0);

  const eventDate = event.startAt
    ? (() => {
        const start = formatDate(event.startAt);
        if (event.endAt) {
          const end = formatDate(event.endAt);
          return start === end ? start : `${start} – ${end}`;
        }
        return start;
      })()
    : null;

  function itemPhotos(item: PlanningItem): EntityAttachment[] {
    return photosByItem.get(item.id) ?? [];
  }

  function handlePrint() {
    const win = window.open('', '_blank', 'width=900,height=800');
    if (!win) return;

    const thStyle =
      'padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;';
    const tdStyle = 'padding:8px 10px;border-bottom:1px solid #f3f4f6;';

    const photoCellHtml = (item: PlanningItem): string => {
      const imgs = itemPhotos(item)
        .map(
          (a) =>
            `<img src="${esc(a.downloadUrl)}" alt="${esc(a.fileName)}" style="width:56px;height:56px;object-fit:cover;border:1px solid #e5e7eb;border-radius:6px;background:#fff;"/>`,
        )
        .join('');
      return `<td style="${tdStyle}"><div style="display:flex;flex-wrap:wrap;gap:4px;">${imgs || '<span style="color:#d1d5db;">—</span>'}</div></td>`;
    };

    const cellHtml = (col: ColumnKey, item: PlanningItem): string => {
      if (col === 'photo') return photoCellHtml(item);
      const style =
        tdStyle +
        (RIGHT_ALIGNED.has(col) ? 'text-align:right;' : '') +
        (MUTED_COLUMNS.has(col) ? 'color:#6b7280;' : '') +
        (col === 'item' || col === 'total' ? 'font-weight:500;' : '');
      return `<td style="${style}">${esc(columnText(col, item))}</td>`;
    };

    const headHtml = visibleColumns
      .map(
        (col) =>
          `<th style="${thStyle}text-align:${RIGHT_ALIGNED.has(col) ? 'right' : 'left'};">${esc(
            COLUMN_LABELS[col],
          )}</th>`,
      )
      .join('');

    const rowsHtml =
      items.length === 0
        ? `<tr><td colspan="${visibleColumns.length}" style="padding:12px 10px;color:#9ca3af;font-style:italic;">No items.</td></tr>`
        : items
            .map((item) => `<tr>${visibleColumns.map((col) => cellHtml(col, item)).join('')}</tr>`)
            .join('');

    const totalRowStyle = 'padding:9px 10px;border-top:2px solid #e5e7eb;';
    const totalRowHtml =
      showTotalRow && items.length > 0
        ? `<tr style="background:#f9fafb;font-weight:600;">${visibleColumns
            .map((col, idx) => {
              if (idx === 0) return `<td style="${totalRowStyle}">Total</td>`;
              if (col === 'total')
                return `<td style="${totalRowStyle}text-align:right;">${esc(fmt(grandTotal))}</td>`;
              return `<td style="${totalRowStyle}"></td>`;
            })
            .join('')}</tr>`
        : '';

    const eventMeta = [eventDate, event.venueName, event.eventTypeName]
      .filter(Boolean)
      .map((line) => `<div>${esc(String(line))}</div>`)
      .join('');

    const filtersHtml =
      filters.length === 0
        ? ''
        : `<div style="margin-bottom:20px;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;font-size:12px;color:#374151;">
            <span style="font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:0.05em;color:#6b7280;margin-right:8px;">Filtered by</span>
            ${filters
              .map(
                (f) =>
                  `<span style="margin-right:14px;"><strong>${esc(f.label)}:</strong> ${esc(f.values.join(', '))}</span>`,
              )
              .join('')}
          </div>`;

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Itemized List — ${esc(event.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; padding: 36px; color: #111827; font-size: 13px; line-height: 1.5; }
    img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @media print {
      body { padding: 20px; }
      @page { margin: 1.5cm; size: A4; }
      tr { break-inside: avoid; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div style="display:flex;align-items:flex-start;gap:20px;padding-bottom:20px;border-bottom:1px solid #e5e7eb;margin-bottom:20px;">
    <img src="${esc(logoSrc)}" alt="Logo"
      style="width:64px;height:64px;object-fit:contain;border:1px solid #e5e7eb;border-radius:8px;flex-shrink:0;background:#fff;"
      onerror="this.src='/logo.png'"/>
    <div>
      <div style="font-size:20px;font-weight:700;color:#111827;margin-bottom:4px;">${esc(event.name)}</div>
      <div style="font-size:13px;color:#6b7280;line-height:1.7;">${eventMeta}</div>
    </div>
    <div style="margin-left:auto;text-align:right;">
      <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#6b7280;">Itemized List</div>
      <div style="font-size:12px;color:#9ca3af;">${items.length} item${items.length !== 1 ? 's' : ''}</div>
    </div>
  </div>

  ${filtersHtml}

  <table style="width:100%;border-collapse:collapse;font-size:13px;">
    <thead>
      <tr>${headHtml}</tr>
    </thead>
    <tbody>
      ${rowsHtml}
      ${totalRowHtml}
    </tbody>
  </table>

</body>
</html>`);

    win.document.close();
    // Wait for the logo + any embedded photos to load before printing.
    printWhenReady(win);
  }

  const printDisabled = photoSelected && photosLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Itemized List</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              disabled={printDisabled}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-md text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="itemized-print-button"
            >
              {printDisabled ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Printer size={14} />
              )}
              {printDisabled ? 'Loading photos…' : 'Print / Save PDF'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable preview */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Column picker */}
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-4 border-b"
            data-testid="itemized-print-columns"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Columns
            </span>
            {availableColumns.map((col) => (
              <label
                key={col}
                className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={selected.has(col)}
                  disabled={col === REQUIRED_COLUMN}
                  onChange={() => toggleColumn(col)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  data-testid={`column-toggle-${col}`}
                />
                {COLUMN_LABELS[col]}
              </label>
            ))}
          </div>

          {/* Header */}
          <div className="flex items-start gap-4 pb-5 border-b">
            <img
              src={logoSrc}
              alt="Logo"
              style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }}
              className="rounded-lg border border-gray-200 bg-white"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/logo.png';
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{event.name}</h1>
              <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                {eventDate && <p>{eventDate}</p>}
                {event.venueName && <p>{event.venueName}</p>}
                {event.eventTypeName && <p>{event.eventTypeName}</p>}
              </div>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Itemized List
              </p>
              <p className="text-xs text-gray-400">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Active filters */}
          {filters.length > 0 && (
            <div
              className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-xs text-gray-700 flex flex-wrap gap-x-4 gap-y-1"
              data-testid="itemized-print-filters"
            >
              <span className="font-semibold uppercase text-[10px] tracking-wide text-gray-500">
                Filtered by
              </span>
              {filters.map((f) => (
                <span key={f.label}>
                  <strong>{f.label}:</strong> {f.values.join(', ')}
                </span>
              ))}
            </div>
          )}

          {photoSelected && photosLoading && (
            <p className="text-xs text-gray-400" data-testid="itemized-photos-loading">
              Loading photos…
            </p>
          )}

          {/* Items table */}
          {items.length === 0 ? (
            <p className="text-sm text-gray-400">No items.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  {visibleColumns.map((col) => (
                    <th
                      key={col}
                      className={`py-2 text-xs text-gray-500 font-semibold uppercase ${
                        RIGHT_ALIGNED.has(col) ? 'text-right' : 'text-left'
                      }`}
                    >
                      {COLUMN_LABELS[col]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    {visibleColumns.map((col) => {
                      if (col === 'photo') {
                        return (
                          <td key={col} className="py-2">
                            <div className="flex flex-wrap gap-1">
                              {itemPhotos(item).length === 0 ? (
                                <span className="text-gray-300">—</span>
                              ) : (
                                itemPhotos(item).map((a) => (
                                  <img
                                    key={a.id}
                                    src={a.downloadUrl ?? ''}
                                    alt={a.fileName ?? ''}
                                    className="w-12 h-12 object-cover rounded border border-gray-200 bg-white"
                                  />
                                ))
                              )}
                            </div>
                          </td>
                        );
                      }
                      const cls = [
                        'py-2',
                        RIGHT_ALIGNED.has(col) ? 'text-right' : '',
                        col === 'item' || col === 'total' ? 'font-medium' : '',
                        MUTED_COLUMNS.has(col) ? 'text-gray-500' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <td key={col} className={cls}>
                          {columnText(col, item)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {showTotalRow && (
                  <tr className="font-semibold bg-gray-50">
                    {visibleColumns.map((col, idx) => {
                      if (idx === 0)
                        return (
                          <td key={col} className="py-2">
                            Total
                          </td>
                        );
                      if (col === 'total')
                        return (
                          <td key={col} className="py-2 text-right">
                            {fmt(grandTotal)}
                          </td>
                        );
                      return <td key={col} className="py-2" />;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
